import { useQuery, ApolloQueryResult, ApolloError } from '@apollo/client';
import { useMemo } from 'react';
import { getPropertyTenantDomainsQuery } from 'graphql-queries';

const DEFAULT_FIRST = 25;

/**
 * Returns true only when every GraphQL error in the response is a
 * "Property not found" message (case-sensitive substring match).
 * Mixed responses containing real errors alongside "Property not found"
 * are intentionally NOT suppressed so genuine failures surface to the UI.
 */
function isOnlyPropertyNotFoundError(error: ApolloError | undefined): boolean {
  if (!error?.graphQLErrors?.length) return false;

  return error.graphQLErrors.every(
    (e) => e.message?.includes('Property not found')
  );
}

export interface UsePropertyTenantDomainsResult {
  domains: string[];
  remainingCount: number;
  displayText: string;
  loading: boolean;
  error: Error | null;
  // Apollo's refetch returns a Promise with the query result
  refetch: () => Promise<ApolloQueryResult<unknown>>;
}

/**
 * Hook to fetch aggregated permitted email domains from all tenant channels for a property.
 * Returns paginated union of all unique domains from tenant whitelists with memoization.
 * 
 * @param propertyId - The unique identifier of the property
 * @param first - Number of domains to fetch (default: 25)
 * @returns Object containing domains array, remainingCount, displayText, loading, error, and refetch
 */
export function usePropertyTenantDomains(
  propertyId: string | undefined,
  first: number = DEFAULT_FIRST
): UsePropertyTenantDomainsResult {
  const { data, loading, error, refetch } = useQuery(
    getPropertyTenantDomainsQuery,
    {
      variables: {
        propertyId,
        first,
      },
      skip: !propertyId,
      fetchPolicy: 'cache-and-network',
    }
  );

  // Memoize processed data to prevent unnecessary recalculations
  const domains = useMemo(() => {
    return data?.property?.domains?.edges?.map((edge: { node: { domain: string } }) => edge.node.domain) || [];
  }, [data?.property?.domains?.edges]);

  const total = useMemo(() => {
    return data?.property?.domains?.pageInfo?.total || 0;
  }, [data?.property?.domains?.pageInfo?.total]);

  const remainingCount = useMemo(() => {
    return Math.max(0, total - domains.length);
  }, [total, domains.length]);

  // Memoize display text (domains + remaining count)
  const displayText = useMemo(() => {
    if (domains.length === 0) {
      return '';
    }

    const domainsText = domains.join(', ');

    if (remainingCount > 0) {
      return `${domainsText}, +${remainingCount} more`;
    }

    return domainsText;
  }, [domains, remainingCount]);

  const surfacedError = isOnlyPropertyNotFoundError(error)
    ? null
    : (error ?? null);

  return {
    domains,
    remainingCount,
    displayText,
    loading,
    error: surfacedError,
    refetch,
  };
}

